/**
 * Session adoption + the boot silent refresh (genesis handoff §3.1) + the deep-link
 * stash that survives the round trip through centralized login.
 *
 * The deep link is the ONLY thing this app puts in sessionStorage. Tokens never go there.
 */
import { authApi } from '@/api/auth';
import { expiryFrom } from '@/api/apiAxiosInstance';
import type { AuthTokenResponse } from '@/api/types';
import { setAccessToken, setCsrfToken } from './authStore';

const DEEP_LINK_KEY = 'saas_brand.deepLink';

/**
 * Adopt a token-minting response (`refresh` / `session/exchange`). Captures the access
 * token, its expiry, and the rotated store-token CSRF value. Returns false when the body
 * carried no access token, so callers can treat it as "not authenticated".
 */
export function adoptTokenResponse(body: AuthTokenResponse | null | undefined): boolean {
  if (!body?.accessToken) return false;
  setAccessToken(body.accessToken, expiryFrom(body));
  setCsrfToken(body.csrfToken ?? null);
  return true;
}

/**
 * Cold-boot attempt: if the HttpOnly refresh cookie is still live this mints a session
 * with no redirect. Any failure (401 / 403 / network) simply means "not authenticated" —
 * the caller runs the login kickoff.
 */
export async function bootRefresh(): Promise<boolean> {
  try {
    return adoptTokenResponse(await authApi.refresh());
  } catch {
    return false;
  }
}

export function stashDeepLink(path: string): void {
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* storage disabled or over quota — the deep link is a nice-to-have */
  }
}

/** Read and consume the stashed deep link. */
export function popDeepLink(): string | null {
  try {
    const value = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return value;
  } catch {
    return null;
  }
}
